import { Injectable } from '@angular/core';
import {
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpInterceptor,
    HttpErrorResponse
} from '@angular/common/http';
import { EMPTY, Observable, throwError } from 'rxjs';

import { AppFacadeService } from '@services/app-facade.service';
import { finalize, catchError, map, mergeMap, switchMap, take, delay } from 'rxjs/operators';
import { AuthService } from '@services/auth/auth.service';
import { environment } from "../../../environments/environment";

export const excludeAppLoadingURLs = [
    'version.json',
    'error-review-target',
    'business/',
    'locations/business?id=',
];

/**
 * Handles all API HTTP requests, adds 
 * request headers to them and controls
 * petition errors.
 */
@Injectable({
    providedIn: 'root'
})
export class HeaderInterceptor implements HttpInterceptor {

    /**
     * HeaderInterceptor Constructor
     * @param appFacadeService The Facade service of the app for storaging app errors and handling preloader functionality
     * @param authService The service that manages app authentication system
     */
    constructor(
        private appFacadeService: AppFacadeService,
        private authService: AuthService
    ) { }

    /**
     * This method performs three functionalities:
     * - Appends Content-Type header to all HTTP request and adds Authorization header to all HTTP requests that are different from users/config, users/signup, resellers/domain and maintenance/logger.
     * - Handles the preoloader functionality when a request returns a value.
     * - Catches petition errors. Currently the system manages the expiration token error "Expired JWT" and refreshes the logged user session.
     * @param request
     * @param next 
     */
    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (!excludeAppLoadingURLs.some(url => request.url.includes(url))) {
            this.appFacadeService.executeTaskApp()
        }
        const token = localStorage.getItem('token')
        request = request.clone({
            headers: request.headers.append('Content-Type', 'application/json')
        });
        if (!(
            request.url.includes('users/config') ||
            request.url.includes('users/signup') ||
            request.url.includes('resellers/domain?url=') || 
            request.url.includes('users/exist') ||
            request.url.includes('maintenance/logger') ||
            request.url.includes('cognito-log') ||
            request.url.includes('prod-reviewshake') ||
            request.url.includes('demo-reviewshake') ||
            request.url.includes('omega-reviewshake') ||
            request.url.includes('sigma-reviewshake')
        )) {
            request = request.clone({
                headers: request.headers.append('Authorization', `Bearer ${token}`),
            });
        }
        return next.handle(request).pipe(
            finalize(() => {
                if (!excludeAppLoadingURLs.some(url => request.url.includes(url))) {
                    this.appFacadeService.executeTaskAppSuccess()
                }
            }),
            catchError((error: HttpErrorResponse) => {
                if ("Expired JWT" == error.error["error"]) {
                    console.warn('[header.interceptor catchError] Expired JWT detected. Attempting refresh...')

                    return this.authService.currentSession().pipe(
                        delay(1000), // resolves infinite loop issue in some browsers
                        map(data => data["idToken"]["jwtToken"]),
                        mergeMap(newToken => {
                            localStorage.setItem("token", newToken)
                            request = request.clone({
                                setHeaders: {
                                    Authorization: `Bearer ${newToken}`
                                }
                            });
                            return next.handle(request);
                        })
                    )
                }

                if (request.url.includes("locations/search")) {
                    return EMPTY as Observable<HttpEvent<any>>;
                }

                return throwError(() => error);
            }),
            catchError((error: HttpErrorResponse) => {
                const data = {
                    reason: error.error.message ? error.error.message : '',
                    status: error.status,
                    error,
                };

                // appError should reset isTaskExecuting
                this.appFacadeService.appError(data.reason, request.url)

                if (environment.showConsole) console.log("HTTP Error:", data);

                return throwError(() => error);
            })
        );
    }
}
